import type { Table } from "@tanstack/react-table";
import {
  columnFilteringFeature,
  columnGroupingFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createFilteredRowModel,
  createSortedRowModel,
  globalFilteringFeature,
  metaHelper,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
} from "@tanstack/react-table";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import React, { useMemo } from "react";

/**
 * Per-column extras the grid reads off `columnDef.meta`.
 *
 * TanStack v9 resolves this through the `columnMeta` slot on the feature
 * bundle below instead of a global `declare module` augmentation, so
 * installing the data grid no longer widens `ColumnMeta` for every other
 * table in the consuming app.
 */
export interface DataListColumnMeta {
  headerTitle?: string;
  skeleton?: ReactNode;
  listLayout?: "primary" | "secondary" | "kicker" | "inline" | "labeled";
}

/**
 * The batteries-included feature bundle.
 * v9 requires each table to declare its features up front, and the grid's
 * render path needs the ones registered here: `columnVisibilityFeature` alone
 * gates `row.getVisibleCells()`, so even a grid that never hides a column
 * needs it to render at all.
 *
 * Pass it straight through for the full grid:
 *
 * ```tsx
 * const table = useTable({ features: dataGridFeatures, columns, data })
 * ```
 *
 * Extend it when a grid needs more, keeping each prerequisite feature ahead of
 * the slot that depends on it:
 *
 * ```tsx
 * const features = tableFeatures({
 *   ...dataGridFeatures,
 *   columnGroupingFeature,
 *   groupedRowModel: createGroupedRowModel(),
 * })
 * ```
 *
 * Or drop it entirely and hand `<DataGrid>` a leaner table - the components
 * accept any bundle, so you keep full ownership of the TanStack core.
 */
export const dataListFeatures = tableFeatures({
  columnVisibilityFeature,
  columnOrderingFeature,
  columnGroupingFeature,
  columnPinningFeature,
  columnSizingFeature,
  columnFilteringFeature,
  // globalFilteringFeature requires columnFilteringFeature, declared above.
  globalFilteringFeature,
  rowSortingFeature,
  rowSelectionFeature,
  sortedRowModel: createSortedRowModel(),
  filteredRowModel: createFilteredRowModel(),
  columnMeta: metaHelper<DataListColumnMeta>(),
});

/** The feature set `dataGridFeatures` registers. */
export type DataListFeatures = typeof dataListFeatures;

interface DataListContextProps {
  isLoading?: boolean;
  pageSize: number;
  renderRow?: ({
    id,
    children,
  }: {
    id: string;
    children: ReactNode;
  }) => ReactElement;
  tableLayout?: {
    headerSticky?: boolean;
  };
}

const DataListContext = React.createContext<DataListContextProps | null>(null);

export function useDataList() {
  const context = React.useContext(DataListContext);
  if (!context) {
    throw new Error("useDataList must be used within a DataListProvider.");
  }

  return context;
}

export function DataListProvider({
  children,
  ...props
}: DataListContextProps & {
  children?: ReactNode;
}) {
  const value = useMemo(
    () => ({
      isLoading: props.isLoading ?? false,
      pageSize: props.pageSize,
      renderRow: props.renderRow,
      tableLayout: props.tableLayout ?? { headerSticky: false },
    }),

    [props.isLoading, props.pageSize, props.renderRow, props.tableLayout],
  );

  return (
    <DataListContext.Provider value={value}>
      {children}
    </DataListContext.Provider>
  );
}

export function DataList({
  children,
  ...props
}: {
  children?: ReactNode;
} & DataListContextProps) {
  return <DataListProvider {...props}>{children}</DataListProvider>;
}

export function DataListContent<TData extends object>({
  table,
  style,
}: {
  table: Table<DataListFeatures, TData>;
  style?: CSSProperties;
}) {
  return <DataListContainer table={table} style={style} />;
}

function DataListContainer<TData extends object>({
  //table,
  style,
}: {
  table: Table<DataListFeatures, TData>;
  style?: CSSProperties;
}) {
  return (
    <div className="data-list-container" style={style}>
      {/* Render your virtualized rows or other content here */}
    </div>
  );
}
